import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import './index.css';
import Configurator, {
  EmbedFilters
} from '../../configurator-ui/src/features/app/Configurator';
import {
  partnerStore,
  usePartnerDispatch,
  usePartnerSelector,
  selectProducts,
  selectActiveProduct
} from './store';
import {
  addLocalCartItem,
  selectLocalCartTotalCents
} from './store/localCartSlice';
import { initMultiProduct } from './store/multiProductSlice';
import { CartIconButton } from './features/cart/CartIconButton';
import { LocalCartTab } from './features/cart/LocalCartTab';
import { SelectProductTab } from './features/selectProduct/SelectProductTab';
import { fetchShopifyProductInfo } from './utils/api';
import { formatPrice } from './utils/formatPrice';
import { ParsedProductConfig } from './types';

/**
 * Embed Configurator Entry Point
 *
 * This renders the product configurator for embedding in external websites.
 * Configuration is passed via URL parameters:
 * - product_tag: The product to configure (required)
 * - partner_key: Affiliate partner key (optional)
 * - product_title, product_description, price_min, price_max, currency: Product info (optional)
 * - styles, colors, fonts, icons, materials: Comma-separated filter lists (optional)
 */

interface ProductInfo {
  title: string;
  priceMin: string;
  priceMax: string;
  currency: string;
}

function getContrastColor(
  hex: string | null | undefined,
  light = '#ffffff',
  dark = '#111827'
): string {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return light;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.62 ? dark : light;
}

function sanitizeDescription(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  div.querySelectorAll('a').forEach(a => {
    a.replaceWith(...Array.from(a.childNodes));
  });
  // Shopify's rich-text editor inlines margin/padding:0 on block elements,
  // which overrides prose styles and collapses paragraph spacing.
  div.querySelectorAll('p, h1, h2, h3, h4, h5, h6, ul, ol, li').forEach(el => {
    const style = (el as HTMLElement).style;
    style.removeProperty('margin');
    style.removeProperty('margin-top');
    style.removeProperty('margin-bottom');
    style.removeProperty('padding');
    style.removeProperty('padding-top');
    style.removeProperty('padding-bottom');
  });
  return div.innerHTML;
}

function formatProductHeaderPrice(productInfo: ProductInfo): string {
  const currency = productInfo.currency || 'USD';
  const min = parseFloat(productInfo.priceMin);
  const max = parseFloat(productInfo.priceMax);

  if (Number.isNaN(min) || Number.isNaN(max)) {
    return formatPrice(0, currency);
  }

  return min === max
    ? formatPrice(Math.round(min * 100), currency)
    : `${formatPrice(Math.round(min * 100), currency)} - ${formatPrice(
        Math.round(max * 100),
        currency
      )}`;
}

function EmbedConfigurator() {
  const [activeTab, setActiveTab] = useState<
    'selectProduct' | 'customize' | 'moreInfo' | 'cart'
  >('customize');
  const dispatch = usePartnerDispatch();

  // Multi-product state
  const products = usePartnerSelector(selectProducts);
  const activeProduct = usePartnerSelector(selectActiveProduct);
  const localCartTotalCents = usePartnerSelector(selectLocalCartTotalCents);
  const isMultiProduct = products.length >= 2;
  const isMobileView = useMediaQuery({ maxWidth: 800 });

  // Parse URL parameters
  const params = new URLSearchParams(window.location.search);
  const autoResizeEnabled = params.get('auto_resize') === '1';

  // Parse brand name from ap blob
  let apDecoded: Record<string, string> = {};
  try {
    const apRaw = getBlobParam('ap');
    if (apRaw) apDecoded = JSON.parse(atob(apRaw));
  } catch (err) {
    console.warn('Could not detect appearance parameters', err);
  }
  const brandName = apDecoded.bn;

  const handleEmbedAddToCart = useCallback(
    (item: {
      variantId: number;
      quantity: number;
      properties: Record<string, string>;
    }) => {
      const title = isMultiProduct
        ? activeProduct?.productTitle || 'Product'
        : params.get('product_title') || 'Product';
      const priceMin = isMultiProduct
        ? activeProduct?.productPriceMin || '0'
        : params.get('price_min') || '0';
      const priceCents = Math.round(parseFloat(priceMin) * 100);
      const compareAtPriceMinStr = isMultiProduct
        ? activeProduct?.productCompareAtPriceMin
        : params.get('compare_at_price_min');
      const compareAtPriceCents = compareAtPriceMinStr
        ? Math.round(parseFloat(compareAtPriceMinStr) * 100)
        : undefined;
      dispatch(
        addLocalCartItem({
          localId: crypto.randomUUID(),
          variantId: item.variantId,
          quantity: 1,
          properties: item.properties,
          displayName: title,
          priceCents,
          compareAtPriceCents:
            compareAtPriceCents && compareAtPriceCents > priceCents
              ? compareAtPriceCents
              : undefined
        })
      );
      setActiveTab('cart');
    },
    [activeProduct, dispatch, isMultiProduct, setActiveTab] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const urlProductTag = params.get('product_tag');
  const partnerId = params.get('partner_id') ?? undefined;
  const effectiveShopifyVariantId = isMultiProduct
    ? activeProduct?.shopifyVariantId
    : params.get('shopify_variant_id') ?? undefined;

  // In multi-product mode, drive product_tag and filters from the Redux active product.
  // Fall back to URL params for single-product (backward compat).
  const effectiveProductTag = isMultiProduct
    ? activeProduct?.productTag ?? urlProductTag
    : urlProductTag;

  // Normalize: empty arrays mean "no restriction" — pass undefined so the
  // Configurator treats them as "all allowed" rather than "none allowed".
  const nonEmpty = (arr?: string[]): string[] | undefined =>
    arr && arr.length > 0 ? arr : undefined;

  const effectiveFilters: EmbedFilters =
    isMultiProduct && activeProduct
      ? {
          allowedStyles: nonEmpty(activeProduct.allowedStyles),
          allowedColors: nonEmpty(activeProduct.allowedColors),
          allowedFonts: nonEmpty(activeProduct.allowedFonts),
          allowedIcons: nonEmpty(activeProduct.allowedIcons),
          allowedMaterials: nonEmpty(activeProduct.allowedMaterials)
        }
      : {
          allowedStyles: params.get('styles')?.split(',').filter(Boolean),
          allowedColors: params.get('colors')?.split(',').filter(Boolean),
          allowedFonts: params.get('fonts')?.split(',').filter(Boolean),
          allowedIcons: params.get('icons')?.split(',').filter(Boolean),
          allowedMaterials: params.get('materials')?.split(',').filter(Boolean)
        };

  // In multi-product mode, product info comes from the active product config.
  const productInfo: ProductInfo | null =
    isMultiProduct && activeProduct
      ? {
          title: activeProduct.productTitle ?? '',
          priceMin: activeProduct.productPriceMin ?? '',
          priceMax: activeProduct.productPriceMax ?? '',
          currency: activeProduct.productCurrency ?? 'USD'
        }
      : params.get('product_title')
      ? {
          title: params.get('product_title') || '',
          priceMin: params.get('price_min') || '',
          priceMax: params.get('price_max') || '',
          currency: params.get('currency') || 'USD'
        }
      : null;

  // Fetch product description; re-fetches whenever the active product changes (4.8).
  const [productDescription, setProductDescription] = useState<string | null>(
    null
  );
  const [descriptionLoading, setDescriptionLoading] = useState(true);
  useEffect(() => {
    if (!effectiveProductTag) return;
    setDescriptionLoading(true);
    fetchShopifyProductInfo([effectiveProductTag])
      .then(info =>
        setProductDescription(info[effectiveProductTag]?.description ?? '')
      )
      .catch(() => setProductDescription(''))
      .finally(() => setDescriptionLoading(false));
  }, [effectiveProductTag]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (window.parent === window) return;
    if (!autoResizeEnabled) return;

    const postHeight = () => {
      const root = document.getElementById('embed-root');
      const documentHeight = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight,
        root?.scrollHeight ?? 0
      );

      window.parent.postMessage(
        {
          type: 'namebubbles:embed-resize',
          height: Math.ceil(documentHeight)
        },
        '*'
      );
    };

    postHeight();

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(postHeight);
    });

    resizeObserver.observe(document.body);
    resizeObserver.observe(document.documentElement);
    const root = document.getElementById('embed-root');
    if (root) resizeObserver.observe(root);

    window.addEventListener('load', postHeight);
    window.addEventListener('resize', postHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('load', postHeight);
      window.removeEventListener('resize', postHeight);
    };
  }, [
    activeTab,
    autoResizeEnabled,
    effectiveProductTag,
    isMobileView,
    productDescription
  ]);

  if (!effectiveProductTag) {
    return (
      <div
        style={{
          padding: '20px',
          fontFamily: 'Poppins, sans-serif',
          textAlign: 'center'
        }}
      >
        <h2>Configuration Error</h2>
        <p>Missing required parameter: product_tag</p>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Brand collaboration banner */}
      {brandName && (
        <div
          style={{
            padding: '8px 24px',
            backgroundColor: 'var(--nb-brand-banner-bg, #014876)',
            textAlign: 'center'
          }}
        >
          <p
            style={{
              fontSize: '13px',
              fontWeight: 600,
              color: 'var(--nb-brand-banner-text, #ffffff)',
              margin: 0,
              letterSpacing: '0.05em'
            }}
          >
            {brandName} x Name Bubbles
          </p>
        </div>
      )}

      {/* Product Name and Price - Above tabs */}
      {productInfo && (
        <div
          style={{
            padding: isMobileView ? '14px 16px' : '16px 24px',
            borderBottom: '1px solid var(--nb-border, #e5e7eb)',
            backgroundColor: 'var(--nb-header-bg, #f9fafb)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: isMobileView ? 'flex-start' : 'center',
            flexDirection: isMobileView ? 'column' : 'row',
            gap: '16px'
          }}
        >
          <h2
            style={{
              fontSize: '18px',
              fontWeight: 700,
              color: 'var(--nb-text, #424242)',
              margin: 0,
              minWidth: 0,
              lineHeight: 1.4
            }}
          >
            {`${productInfo.title} - ${formatProductHeaderPrice(productInfo)}`}
          </h2>
          <p
            style={{
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--nb-text, #424242)',
              margin: 0,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              alignSelf: isMobileView ? 'flex-start' : 'auto'
            }}
          >
            {`Cart Total: ${formatPrice(
              localCartTotalCents,
              productInfo.currency
            )}`}
          </p>
        </div>
      )}

      {/* Tabs */}
      <div
        style={{
          borderBottom: '1px solid var(--nb-border, #e5e7eb)',
          backgroundColor: 'var(--nb-body-bg, #ffffff)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'stretch', minWidth: 0 }}>
          {/* Select Product tab — only shown when 2+ products configured (4.4) */}
          {isMultiProduct && (
            <button
              onClick={() => setActiveTab('selectProduct')}
              style={{
                flex: 1,
                padding: isMobileView ? '14px 8px' : '16px 12px',
                fontSize: isMobileView ? '13px' : '14px',
                fontWeight: 600,
                borderBottom:
                  activeTab === 'selectProduct'
                    ? '2px solid var(--nb-accent, #014876)'
                    : 'none',
                color:
                  activeTab === 'selectProduct'
                    ? 'var(--nb-accent, #014876)'
                    : 'var(--nb-text-secondary, #6b7280)',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Select Product
            </button>
          )}
          <button
            onClick={() => setActiveTab('customize')}
            style={{
              flex: 1,
              padding: isMobileView ? '14px 10px' : '16px 24px',
              fontSize: isMobileView ? '13px' : '14px',
              fontWeight: 600,
              borderBottom:
                activeTab === 'customize'
                  ? '2px solid var(--nb-accent, #014876)'
                  : 'none',
              color:
                activeTab === 'customize'
                  ? 'var(--nb-accent, #014876)'
                  : 'var(--nb-text-secondary, #6b7280)',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Customize
          </button>
          <button
            onClick={() => setActiveTab('moreInfo')}
            style={{
              flex: 1,
              padding: isMobileView ? '14px 10px' : '16px 24px',
              fontSize: isMobileView ? '13px' : '14px',
              fontWeight: 600,
              borderBottom:
                activeTab === 'moreInfo'
                  ? '2px solid var(--nb-accent, #014876)'
                  : 'none',
              color:
                activeTab === 'moreInfo'
                  ? 'var(--nb-accent, #014876)'
                  : 'var(--nb-text-secondary, #6b7280)',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            More Info
          </button>
          <CartIconButton
            onClick={() => setActiveTab('cart')}
            isActive={activeTab === 'cart'}
          />
        </div>
      </div>

      {/* Tab Content */}
      <div
        style={{
          backgroundColor: 'var(--nb-body-bg, #ffffff)'
        }}
      >
        {/* Select Product tab (4.5) */}
        {activeTab === 'selectProduct' && (
          <SelectProductTab onProductSelect={() => setActiveTab('customize')} />
        )}

        {activeTab === 'customize' && (
          <Configurator
            product_tag={effectiveProductTag}
            partnerId={partnerId}
            embedShopifyVariantId={effectiveShopifyVariantId}
            show={true}
            embedFilters={effectiveFilters}
            isEmbedMode={true}
            onEmbedAddToCart={handleEmbedAddToCart}
          />
        )}

        {activeTab === 'moreInfo' && (
          <div style={{ padding: isMobileView ? '16px' : '24px' }}>
            <h3
              style={{
                fontSize: '16px',
                fontWeight: 600,
                color: 'var(--nb-text, #424242)',
                marginBottom: '16px'
              }}
            >
              Product Description
            </h3>
            {descriptionLoading ? (
              <p
                style={{
                  fontSize: '14px',
                  color: 'var(--nb-text-secondary, #6b7280)'
                }}
              >
                Loading…
              </p>
            ) : productDescription ? (
              <div
                className="prose prose-sm max-w-none"
                style={{ color: 'var(--nb-text, #374151)' }}
                dangerouslySetInnerHTML={{
                  __html: sanitizeDescription(productDescription)
                }}
              />
            ) : (
              <p
                style={{
                  fontSize: '14px',
                  color: 'var(--nb-text-secondary, #6b7280)'
                }}
              >
                No description available for this product.
              </p>
            )}
          </div>
        )}

        {activeTab === 'cart' && (
          <LocalCartTab
            onCreateAnotherLabelSet={() => setActiveTab('customize')}
          />
        )}
      </div>
    </div>
  );
}

// Module-level URL param parsing — runs once before React renders.
const _moduleParams = new URLSearchParams(window.location.search);

// Large base64 JSON blobs (`products`, `ap`) are generated into the URL fragment
// (see urlGenerator.ts) so they never count against CDN/browser request-line
// length limits, since fragments are never sent over HTTP. Older generated embed
// codes may still carry them in the query string, so fall back to `_moduleParams`.
const _hashParams = new URLSearchParams(window.location.hash.slice(1));
function getBlobParam(name: string): string | null {
  return _hashParams.get(name) ?? _moduleParams.get(name);
}

/**
 * Decoded multi-product config array from the `products` URL param.
 * Contains 2+ entries when a multi-product embed is configured; empty array otherwise.
 * Exported so the Redux store can initialise the multiProduct slice from it (task 3).
 */
export const parsedProducts: ParsedProductConfig[] = (() => {
  try {
    const raw = getBlobParam('products');
    if (!raw) return [];
    const decoded = JSON.parse(atob(raw));
    if (!Array.isArray(decoded) || decoded.length < 2) return [];
    return decoded as ParsedProductConfig[];
  } catch {
    return [];
  }
})();

// Initialise the multiProduct slice before React renders so all components
// get the correct products/activeProductTag on first render.
if (parsedProducts.length >= 2) {
  // `preview_product_tag` is a dashboard-only hint that overrides the initial
  // active product shown in the live preview (takes precedence over isDefault).
  const previewProductTag = _moduleParams.get('preview_product_tag');
  const previewProduct = previewProductTag
    ? parsedProducts.find(p => p.productTag === previewProductTag)
    : null;
  const defaultProduct =
    previewProduct ??
    parsedProducts.find(p => p.isDefault) ??
    parsedProducts[0];
  partnerStore.dispatch(
    initMultiProduct({
      products: parsedProducts,
      defaultProductTag: defaultProduct.productTag
    })
  );
}

// Appearance setup: runs before ReactDOM.createRoot() to avoid FOUC
const appearanceParams = _moduleParams;

// Decode base64 appearance blob (ap) if present; fall back to individual params
let ap: Record<string, string> = {};
const apParam = getBlobParam('ap');
if (apParam) {
  try {
    ap = JSON.parse(atob(apParam));
  } catch {
    /* ignore malformed */
  }
}
const appearanceFontFamily = ap.ff ?? appearanceParams.get('font_family');

if (appearanceFontFamily && appearanceFontFamily !== 'custom') {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
    appearanceFontFamily
  )}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

const rootElement = document.getElementById('embed-root');
if (rootElement) {
  const brandBannerColor = ap.br;
  const accentColor = ap.ac;
  const primaryButtonColor = ap.pb;
  const primaryButtonText = ap.pt;
  const secondaryButtonColor = ap.sb;
  const secondaryButtonText = ap.st;
  const customFontClass = ap.fc ?? appearanceParams.get('custom_font_class');

  if (brandBannerColor) {
    rootElement.style.setProperty('--nb-brand-banner-bg', brandBannerColor);
    rootElement.style.setProperty(
      '--nb-brand-banner-text',
      getContrastColor(brandBannerColor)
    );
  }
  if (accentColor) {
    rootElement.style.setProperty('--nb-accent', accentColor);
    rootElement.style.setProperty(
      '--nb-accent-contrast',
      getContrastColor(accentColor)
    );
  }
  if (primaryButtonColor) {
    rootElement.style.setProperty('--nb-primary-button-bg', primaryButtonColor);
  }
  if (primaryButtonText) {
    rootElement.style.setProperty(
      '--nb-primary-button-text',
      primaryButtonText
    );
  }
  if (secondaryButtonColor) {
    rootElement.style.setProperty(
      '--nb-secondary-button-bg',
      secondaryButtonColor
    );
  }
  if (secondaryButtonText) {
    rootElement.style.setProperty(
      '--nb-secondary-button-text',
      secondaryButtonText
    );
  }
  if (appearanceFontFamily && appearanceFontFamily !== 'custom') {
    rootElement.style.setProperty(
      '--nb-font',
      `'${appearanceFontFamily}', sans-serif`
    );
  }
  if (customFontClass && /^[a-zA-Z0-9_-]+$/.test(customFontClass)) {
    rootElement.classList.add(customFontClass);
  }

  const headerBgColor = ap.hb ?? appearanceParams.get('header_bg_color');
  const bodyBgColor = ap.bb ?? appearanceParams.get('body_bg_color');
  const borderColor = ap.bd ?? appearanceParams.get('border_color');
  const textColor = ap.tc ?? appearanceParams.get('text_color');
  const secondaryTextColor = ap.ts;
  if (headerBgColor)
    rootElement.style.setProperty('--nb-header-bg', headerBgColor);
  if (bodyBgColor) rootElement.style.setProperty('--nb-body-bg', bodyBgColor);
  if (borderColor) rootElement.style.setProperty('--nb-border', borderColor);
  if (textColor) rootElement.style.setProperty('--nb-text', textColor);
  if (secondaryTextColor)
    rootElement.style.setProperty('--nb-text-secondary', secondaryTextColor);

  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <Provider store={partnerStore}>
        <EmbedConfigurator />
      </Provider>
    </React.StrictMode>
  );
}
